import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import { Room, SortMode, GameMode, ChoiceCount, Difficulty } from './types';
import { getRooms } from './rooms';
import { roomFromPath, roomPath } from './roomRoutes';

const CHOICE_COUNT_OPTIONS: ChoiceCount[] = ['all', 4, 6, 8, 12];
const ANSWER_REVEAL_MS = 2000;

type AnswerFeedback = {
  correctId: number;
  selectedId: number;
};

type TouchGesture = {
  mode: 'pinch';
  startDistance: number;
  startZoom: number;
  contentPoint: { x: number; y: number };
} | {
  mode: 'pan';
  startPoint: { x: number; y: number };
  startPan: { x: number; y: number };
};

function getImagePath(room: Room): string {
  return `/rooms/${room.roomIdHex.slice(2).toLowerCase()}.png`;
}

function areaClass(areaName: string): string {
  return `area-${areaName.toLowerCase().replace(/\s+/g, '-')}`;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildAnswerChoices(currentRoom: Room, pool: Room[], choiceCount: Exclude<ChoiceCount, 'all'>): Room[] {
  const wrongRooms = shuffle(pool.filter(r => r.roomId !== currentRoom.roomId)).slice(0, choiceCount - 1);
  return shuffle([currentRoom, ...wrongRooms]);
}

function isSaveRoom(r: Room): boolean {
  const n = r.name.toLowerCase();
  return n.includes('save');
}

function isStation(r: Room): boolean {
  const n = r.name.toLowerCase();
  return n.includes('recharge') || n.includes('refill') ||
    (n.includes('station') && (n.includes('energy') || n.includes('missile')));
}

function isMapRoom(r: Room): boolean {
  const n = r.name.toLowerCase();
  return n.includes('map');
}

function sortRooms(rooms: Room[], mode: SortMode): Room[] {
  const sorted = [...rooms];
  switch (mode) {
    case 'id':
      return sorted.sort((a, b) => a.roomId - b.roomId);
    case 'area':
      return sorted.sort((a, b) => a.areaName.localeCompare(b.areaName) || a.roomId - b.roomId);
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
  }
}

function App() {
  const [allRooms] = useState<Room[]>(() => getRooms());
  const [sortMode, setSortMode] = useState<SortMode>('id');
  const [searchQuery, setSearchQuery] = useState('');
  const [gameMode, setGameMode] = useState<GameMode>('one-pass');
  const [choiceCount, setChoiceCount] = useState<ChoiceCount>(4);
  const [difficulty, setDifficulty] = useState<Difficulty>('easy');
  const [includeSaves, setIncludeSaves] = useState(false);
  const [includeStations, setIncludeStations] = useState(false);
  const [includeMapRooms, setIncludeMapRooms] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);

  // Game state
  const [playing, setPlaying] = useState(false);
  const [remaining, setRemaining] = useState<Room[]>([]);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(() =>
    roomFromPath(allRooms, window.location.pathname)
  );
  const [correct, setCorrect] = useState(0);
  const [misses, setMisses] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [guessedIds, setGuessedIds] = useState<Set<number>>(new Set());
  const [gameOver, setGameOver] = useState(false);
  const [answerChoices, setAnswerChoices] = useState<Room[]>([]);
  const [answerFeedback, setAnswerFeedback] = useState<AnswerFeedback | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resizable panel
  const [panelWidth, setPanelWidth] = useState(395);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Zoom & pan
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const touchGestureRef = useRef<TouchGesture | null>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const playableRooms = useMemo(() => {
    let filtered = allRooms;
    if (!includeSaves) filtered = filtered.filter(r => !isSaveRoom(r));
    if (!includeStations) filtered = filtered.filter(r => !isStation(r));
    if (!includeMapRooms) filtered = filtered.filter(r => !isMapRoom(r));
    return filtered;
  }, [allRooms, includeSaves, includeStations, includeMapRooms]);

  const limitedChoicesActive = playing && choiceCount !== 'all';

  const setBrowserRoomPath = useCallback((room: Room | null, replace = false) => {
    const path = room ? roomPath(room) : '/';
    if (window.location.pathname === path) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', path);
  }, []);

  const selectRoom = useCallback((room: Room) => {
    setCurrentRoom(room);
    setBrowserRoomPath(room);
  }, [setBrowserRoomPath]);

  const clearPendingTransition = useCallback(() => {
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPendingTransition, [clearPendingTransition]);

  useEffect(() => {
    const handlePopState = () => {
      clearPendingTransition();
      setPlaying(false);
      setRemaining([]);
      setCorrect(0);
      setMisses(0);
      setSkipped(0);
      setGuessedIds(new Set());
      setGameOver(false);
      setAnswerChoices([]);
      setAnswerFeedback(null);
      setIsResolving(false);
      setMobileSettingsOpen(false);
      setCurrentRoom(roomFromPath(allRooms, window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [allRooms, clearPendingTransition]);

  useEffect(() => {
    document.title = currentRoom && !playing
      ? `${currentRoom.name} | Super Metroid Rooms`
      : 'Super Metroid Rooms';
  }, [currentRoom, playing]);

  // Pick next room from remaining
  const pickNext = useCallback((pool: Room[]) => {
    setAnswerFeedback(null);
    if (pool.length === 0) {
      setCurrentRoom(null);
      setGameOver(true);
      setAnswerChoices([]);
      return;
    }
    const idx = Math.floor(Math.random() * pool.length);
    setCurrentRoom(pool[idx]);
  }, []);

  // Start game
  const startGame = useCallback(() => {
    clearPendingTransition();
    setBrowserRoomPath(null, true);
    const shuffled = shuffle(playableRooms);
    setRemaining(shuffled);
    setCurrentRoom(null);
    setCorrect(0);
    setMisses(0);
    setSkipped(0);
    setGuessedIds(new Set());
    setGameOver(false);
    setPlaying(true);
    setAnswerChoices([]);
    setAnswerFeedback(null);
    setIsResolving(false);
    setMobileSettingsOpen(false);
    pickNext(shuffled);
  }, [clearPendingTransition, pickNext, playableRooms, setBrowserRoomPath]);

  // Stop and return to room-browsing mode
  const stopGame = useCallback(() => {
    clearPendingTransition();
    setBrowserRoomPath(currentRoom, true);
    setPlaying(false);
    setRemaining([]);
    setCorrect(0);
    setMisses(0);
    setSkipped(0);
    setGuessedIds(new Set());
    setGameOver(false);
    setAnswerChoices([]);
    setAnswerFeedback(null);
    setIsResolving(false);
    setMobileSettingsOpen(false);
  }, [clearPendingTransition, currentRoom, setBrowserRoomPath]);

  useEffect(() => {
    if (!limitedChoicesActive || !currentRoom) {
      setAnswerChoices([]);
      return;
    }
    setAnswerChoices(buildAnswerChoices(currentRoom, playableRooms, choiceCount));
  }, [limitedChoicesActive, currentRoom, playableRooms, choiceCount]);

  // Handle a guess
  const handleGuess = useCallback((room: Room) => {
    if (!playing || !currentRoom || gameOver || isResolving) return;
    if (choiceCount === 'all' && guessedIds.has(room.roomId)) return;

    if (room.roomId === currentRoom.roomId) {
      // Correct!
      setIsResolving(true);
      setAnswerFeedback({ correctId: currentRoom.roomId, selectedId: room.roomId });
      setCorrect(c => c + 1);
      setGuessedIds(prev => new Set(prev).add(room.roomId));

      const newRemaining = remaining.filter(r => r.roomId !== room.roomId);
      setRemaining(newRemaining);

      transitionTimeoutRef.current = setTimeout(() => {
        transitionTimeoutRef.current = null;
        setIsResolving(false);
        pickNext(newRemaining);
      }, ANSWER_REVEAL_MS);
    } else {
      // Wrong
      setMisses(m => m + 1);
      setAnswerFeedback({ correctId: currentRoom.roomId, selectedId: room.roomId });
      setIsResolving(true);

      if (gameMode === 'one-pass') {
        // In one-pass, a wrong guess moves on (counts as a miss)
        const newRemaining = remaining.filter(r => r.roomId !== currentRoom.roomId);
        setRemaining(newRemaining);
        transitionTimeoutRef.current = setTimeout(() => {
          transitionTimeoutRef.current = null;
          setIsResolving(false);
          pickNext(newRemaining);
        }, ANSWER_REVEAL_MS);
      } else {
        transitionTimeoutRef.current = setTimeout(() => {
          transitionTimeoutRef.current = null;
          setAnswerFeedback(null);
          setIsResolving(false);
        }, ANSWER_REVEAL_MS);
      }
      // In all-correct mode, stay on the same room
    }
  }, [playing, currentRoom, gameOver, isResolving, choiceCount, guessedIds, remaining, gameMode, pickNext]);

  // Skip
  const handleSkip = useCallback(() => {
    if (!playing || !currentRoom || gameOver || isResolving) return;
    setSkipped(s => s + 1);
    setAnswerFeedback(null);

    if (gameMode === 'one-pass') {
      const newRemaining = remaining.filter(r => r.roomId !== currentRoom.roomId);
      setRemaining(newRemaining);
      pickNext(newRemaining);
    } else {
      // In all-correct, just pick a different room
      pickNext(remaining);
    }
  }, [playing, currentRoom, gameOver, isResolving, remaining, gameMode, pickNext]);

  // Filter and sort rooms for display
  const displayRooms = useMemo(() => {
    let filtered = playableRooms;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.roomIdHex.toLowerCase().includes(q) ||
        r.roomId.toString().includes(q) ||
        r.areaName.toLowerCase().includes(q) ||
        r.handle.toLowerCase().includes(q)
      );
    }
    return sortRooms(filtered, sortMode);
  }, [playableRooms, searchQuery, sortMode]);

  const listedRooms = limitedChoicesActive ? answerChoices : displayRooms;

  // Reset zoom/pan when room changes
  useEffect(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    touchGestureRef.current = null;
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentRoom]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  // Wheel zoom (scroll wheel + trackpad pinch)
  useEffect(() => {
    const el = rightPanelRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.ctrlKey ? -e.deltaY * 0.01 : -e.deltaY * 0.003;
      setZoom(z => Math.max(0.5, Math.min(10, z * (1 + delta))));
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // Touch: two-finger pinch to zoom, then one-finger drag to pan
  useEffect(() => {
    const el = rightPanelRef.current;
    if (!el) return;

    const distanceBetween = (first: Touch, second: Touch) =>
      Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);

    const midpointInPanel = (first: Touch, second: Touch) => {
      const rect = el.getBoundingClientRect();
      return {
        x: (first.clientX + second.clientX) / 2 - rect.left,
        y: (first.clientY + second.clientY) / 2 - rect.top,
      };
    };

    const beginPinch = (touches: TouchList) => {
      const first = touches[0];
      const second = touches[1];
      const midpoint = midpointInPanel(first, second);
      const currentZoom = zoomRef.current;
      const currentPan = panRef.current;
      touchGestureRef.current = {
        mode: 'pinch',
        startDistance: Math.max(1, distanceBetween(first, second)),
        startZoom: currentZoom,
        contentPoint: {
          x: (midpoint.x - currentPan.x) / currentZoom,
          y: (midpoint.y - currentPan.y) / currentZoom,
        },
      };
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (!el.querySelector('.room-image')) return;
      if (event.touches.length >= 2) {
        event.preventDefault();
        beginPinch(event.touches);
      } else if (event.touches.length === 1 && zoomRef.current > 1) {
        event.preventDefault();
        const touch = event.touches[0];
        touchGestureRef.current = {
          mode: 'pan',
          startPoint: { x: touch.clientX, y: touch.clientY },
          startPan: { ...panRef.current },
        };
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        event.preventDefault();
        if (touchGestureRef.current?.mode !== 'pinch') beginPinch(event.touches);
        const gesture = touchGestureRef.current;
        if (!gesture || gesture.mode !== 'pinch') return;

        const first = event.touches[0];
        const second = event.touches[1];
        const midpoint = midpointInPanel(first, second);
        const nextZoom = Math.max(1, Math.min(
          10,
          gesture.startZoom * distanceBetween(first, second) / gesture.startDistance,
        ));
        const nextPan = nextZoom === 1 ? { x: 0, y: 0 } : {
          x: midpoint.x - gesture.contentPoint.x * nextZoom,
          y: midpoint.y - gesture.contentPoint.y * nextZoom,
        };

        zoomRef.current = nextZoom;
        panRef.current = nextPan;
        setZoom(nextZoom);
        setPan(nextPan);
      } else if (event.touches.length === 1 && touchGestureRef.current?.mode === 'pan') {
        event.preventDefault();
        const touch = event.touches[0];
        const gesture = touchGestureRef.current;
        const nextPan = {
          x: gesture.startPan.x + touch.clientX - gesture.startPoint.x,
          y: gesture.startPan.y + touch.clientY - gesture.startPoint.y,
        };
        panRef.current = nextPan;
        setPan(nextPan);
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length === 1 && zoomRef.current > 1) {
        const touch = event.touches[0];
        touchGestureRef.current = {
          mode: 'pan',
          startPoint: { x: touch.clientX, y: touch.clientY },
          startPan: { ...panRef.current },
        };
      } else {
        touchGestureRef.current = null;
      }
    };

    const handleTouchCancel = () => {
      touchGestureRef.current = null;
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    el.addEventListener('touchcancel', handleTouchCancel);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, []);

  // Keyboard: zoom (+/- and 0 to reset), arrow keys to navigate rooms
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        setZoom(z => Math.min(10, z * 1.2));
      } else if (e.key === '-') {
        e.preventDefault();
        setZoom(z => Math.max(0.5, z / 1.2));
      } else if (e.key === '0') {
        e.preventDefault();
        setZoom(1);
        setPan({ x: 0, y: 0 });
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (playing) return;
        const list = displayRooms;
        if (list.length === 0) return;
        if (!currentRoom) {
          selectRoom(e.key === 'ArrowDown' ? list[0] : list[list.length - 1]);
          return;
        }
        const idx = list.findIndex(r => r.roomId === currentRoom.roomId);
        const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
        if (next >= 0 && next < list.length) {
          selectRoom(list[next]);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [displayRooms, currentRoom, playing, selectRoom]);

  // Mouse drag to pan when zoomed
  const handlePanMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return;
    e.preventDefault();
    isPanning.current = true;
    panStart.current = { x: e.clientX, y: e.clientY };
    panOffset.current = { ...pan };
  }, [zoom, pan]);

  useEffect(() => {
    const handlePanMove = (e: MouseEvent) => {
      if (!isPanning.current) return;
      setPan({
        x: panOffset.current.x + (e.clientX - panStart.current.x),
        y: panOffset.current.y + (e.clientY - panStart.current.y),
      });
    };
    const handlePanUp = () => {
      isPanning.current = false;
    };
    document.addEventListener('mousemove', handlePanMove);
    document.addEventListener('mouseup', handlePanUp);
    return () => {
      document.removeEventListener('mousemove', handlePanMove);
      document.removeEventListener('mouseup', handlePanUp);
    };
  }, []);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    setIsDragging(true);
  }, [panelWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.max(200, Math.min(window.innerWidth * 0.6, startWidthRef.current + delta));
      setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      dragRef.current = false;
      setIsDragging(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className={`app ${playing ? 'playing' : ''}`}>
      <div className="left-panel" style={{ width: panelWidth }}>
        <div className="panel-header">
          <h1>Super Metroid Rooms</h1>
          <div className={`controls ${mobileSettingsOpen ? 'mobile-settings-open' : ''}`}>
            <button
              className="mobile-settings-toggle"
              type="button"
              aria-expanded={mobileSettingsOpen}
              onClick={() => setMobileSettingsOpen(open => !open)}
            >
              {mobileSettingsOpen ? 'Close Setup' : 'Setup'}
            </button>
            <div className="mode-buttons setup-only">
              <button className={`mode-btn ${gameMode === 'one-pass' ? 'active' : ''}`} onClick={() => setGameMode('one-pass')}>One Pass</button>
              <button className={`mode-btn ${gameMode === 'all-correct' ? 'active' : ''}`} onClick={() => setGameMode('all-correct')}>All Correct</button>
            </div>
            <div className="filter-label setup-only">Choices:</div>
            <div className="choice-buttons setup-only">
              {CHOICE_COUNT_OPTIONS.map(option => (
                <button
                  key={option}
                  className={`choice-btn ${choiceCount === option ? 'active' : ''}`}
                  onClick={() => setChoiceCount(option)}
                  aria-pressed={choiceCount === option}
                >
                  {option === 'all' ? 'All' : option}
                </button>
              ))}
            </div>
            <div className="filter-label setup-only">Difficulty:</div>
            <div className="difficulty-buttons setup-only">
              <button
                className={`difficulty-btn ${difficulty === 'easy' ? 'active' : ''}`}
                onClick={() => setDifficulty('easy')}
                aria-pressed={difficulty === 'easy'}
              >
                Easy
              </button>
              <button
                className={`difficulty-btn ${difficulty === 'normal' ? 'active' : ''}`}
                onClick={() => setDifficulty('normal')}
                aria-pressed={difficulty === 'normal'}
              >
                Normal
              </button>
            </div>
            <div className="game-controls">
              <button className="game-btn start" onClick={startGame}>
                {playing && !gameOver ? 'Restart' : 'Start Game'}
              </button>
              {playing && !gameOver && (
                <>
                  <button className="game-btn" onClick={handleSkip}>Skip</button>
                  <button className="game-btn stop" onClick={stopGame}>Stop</button>
                </>
              )}
            </div>
            <div className="filter-label setup-only">Include:</div>
            <div className="filter-buttons setup-only">
              <button className={`filter-btn ${includeSaves ? 'active' : ''}`} onClick={() => setIncludeSaves(v => !v)}>Saves</button>
              <button className={`filter-btn ${includeStations ? 'active' : ''}`} onClick={() => setIncludeStations(v => !v)}>Stations</button>
              <button className={`filter-btn ${includeMapRooms ? 'active' : ''}`} onClick={() => setIncludeMapRooms(v => !v)}>Map Rooms</button>
            </div>
            {!limitedChoicesActive && (
              <div className="browse-controls">
                <div className="filter-label">Search:</div>
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search by name, ID, area..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <div className="filter-label">Sort:</div>
                <div className="sort-buttons">
                  <button className={`sort-btn ${sortMode === 'id' ? 'active' : ''}`} onClick={() => setSortMode('id')}>ID</button>
                  <button className={`sort-btn ${sortMode === 'area' ? 'active' : ''}`} onClick={() => setSortMode('area')}>Area</button>
                  <button className={`sort-btn ${sortMode === 'name' ? 'active' : ''}`} onClick={() => setSortMode('name')}>Name</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {playing && (
          <div className="stats">
            <span className="stat-correct">Correct {correct}</span>
            <span className="stat-wrong">Misses {misses}</span>
            <span className="stat-skipped">Skipped {skipped}</span>
            <span className="stat-remaining">Left {remaining.length}</span>
          </div>
        )}

        <div className="room-list">
          {listedRooms.map(room => {
            const hideAnswerHints = playing && difficulty === 'normal';
            const isGuessed = !limitedChoicesActive && guessedIds.has(room.roomId);
            const isRevealedCorrect = answerFeedback?.correctId === room.roomId;
            const isRevealedWrong = answerFeedback?.selectedId === room.roomId && !isRevealedCorrect;
            const isCurrent = currentRoom?.roomId === room.roomId;
            let className = 'room-item';
            if (playing && isRevealedCorrect) className += ' answer-correct';
            else if (playing && isRevealedWrong) className += ' answer-wrong';
            else if (playing && isGuessed) className += ' correct disabled';
            if (playing && isResolving) className += ' resolving';
            if (isCurrent && !playing) className += ' current';

            return (
              <a
                key={room.roomId}
                className={className}
                href={roomPath(room)}
                ref={isCurrent && !playing ? (el) => { el?.scrollIntoView?.({ block: 'nearest' }); } : undefined}
                onClick={event => {
                  if (!playing && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
                  event.preventDefault();
                  if (playing && !gameOver && !isGuessed && !isResolving) {
                    handleGuess(room);
                  } else if (!playing) {
                    selectRoom(room);
                  }
                }}
              >
                <span className={hideAnswerHints ? 'room-name' : `room-name ${areaClass(room.areaName)}`}>
                  {room.name}
                </span>
                <span className="room-meta">
                  {!hideAnswerHints && <>{room.areaName} | </>}
                  {room.roomIdHex}
                  {!hideAnswerHints && <> | {room.widthScreens}x{room.heightScreens}</>}
                </span>
              </a>
            );
          })}
        </div>

        <div
          className={`resize-handle ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleMouseDown}
        />
      </div>

      <div
        className="right-panel"
        ref={rightPanelRef}
        onMouseDown={handlePanMouseDown}
        style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
      >
        {currentRoom ? (
          <div
            className="room-image-container"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'left top',
            }}
          >
            <img
              className="room-image"
              src={getImagePath(currentRoom)}
              alt={currentRoom.name}
              draggable={false}
            />
          </div>
        ) : (
          <span className="no-room">
            {playing ? 'Starting...' : 'Select a room or start a game'}
          </span>
        )}

        {gameOver && (
          <div className="game-over-overlay">
            <h2>Game Over</h2>
            <div className="final-stats">
              <span className="stat-correct">Correct: {correct}</span>
              <span className="stat-wrong">Misses: {misses}</span>
              <span className="stat-skipped">Skipped: {skipped}</span>
            </div>
            <button onClick={startGame}>Play Again</button>
          </div>
        )}

        {playing && !gameOver && currentRoom && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            color: '#333',
            fontSize: 15,
          }}>
            {remaining.length} rooms remaining
          </div>
        )}

        {zoom !== 1 && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            color: '#555',
            fontSize: 14,
            pointerEvents: 'none',
          }}>
            {Math.round(zoom * 100)}% (0 to reset)
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
